import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  BarChart3, TrendingUp, TrendingDown, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, Eye, BookOpen, Activity
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "Kripto sinyali neye göre okunur?",
    answer: "Giriş, TP ve Stop seviyelerine göre değerlendirilir."
  },
  {
    question: "TP seviyeleri ne anlama gelir?",
    answer: "TP seviyeleri olası hedef bölgeleridir."
  },
  {
    question: "Stop Loss neden önemlidir?",
    answer: "Riski sınırlar ve senaryoyu korur."
  },
  {
    question: "LONG ve SHORT farkı nedir?",
    answer: "Fiyat beklentisinin yönüdür. LONG yukarı, SHORT aşağı yönlü senaryodur."
  },
  {
    question: "Sinyaller kesin midir?",
    answer: "Hayır. Olasılık temellidir, kesinlik içermez."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kripto Sinyali Nasıl Okunur? TP, Stop ve Hedef Mantığı",
    "description": "Kripto sinyali nasıl yorumlanır? Giriş seviyesi, TP hedefleri ve Stop Loss mantığını adım adım öğrenin.",
    "author": {
      "@type": "Organization",
      "name": "Borsamex"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://borsamex.com/logo512.png"
      }
    },
    "datePublished": "2025-12-24",
    "dateModified": "2025-12-24",
    "mainEntityOfPage": "https://borsamex.com/rehber/kripto-sinyali-nasil-okunur"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const SinyalNasilOkunurPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Kripto Sinyali Nasıl Okunur? TP, Stop ve Hedef Mantığı | Borsamex"
        description="Kripto sinyali nasıl yorumlanır? Giriş seviyesi, TP hedefleri ve Stop Loss mantığını adım adım öğrenin. Sinyal kartı okuma rehberi."
        keywords="kripto sinyali nasıl okunur, sinyal nasıl yorumlanır, tp stop nasıl kullanılır, al sat sinyal mantığı, sinyal kartı nedir"
        canonical="https://borsamex.com/rehber/kripto-sinyali-nasil-okunur"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Kripto Sinyali Nasıl Okunur?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Pratik</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>6 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Kripto Sinyali Nasıl Okunur?
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                Bir kripto sinyali genellikle <strong className="text-white">giriş seviyesi</strong>, 
                <strong className="text-green-400"> hedef fiyatlar (TP)</strong> ve 
                <strong className="text-red-400"> risk sınırı (Stop Loss)</strong> içerir.
                Sinyali okumak, bu seviyelerin neyi temsil ettiğini anlamakla başlar.
              </p>
              <p className="text-slate-400 mt-4">
                Amaç tahmin yapmak değil, senaryo mantığını kavramaktır.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* Sinyal Neyi Gösterir */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Eye className="h-4 w-4 text-purple-400" />
                </div>
                Kripto Sinyali Neyi Gösterir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Bir sinyal, belirli bir varlık için olası fiyat senaryosunu yapılandırır.
              </p>
              
              <p className="text-slate-300 mb-4">Genellikle şu bileşenlerden oluşur:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>İşlem yönü (LONG / SHORT)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Giriş seviyesi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>TP hedefleri</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Stop Loss seviyesi</span>
                </li>
              </ul>
              
              <Card className="border-slate-700/50 bg-slate-900/30">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Bu yapı bir işlem planı çerçevesidir.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Giriş Seviyesi */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-blue-400" />
                </div>
                Giriş Seviyesi Nasıl Yorumlanır?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Giriş seviyesi, senaryonun başlamasının düşünüldüğü fiyat bölgesidir.
              </p>
              
              <p className="text-slate-300 mb-4">Bu seviye:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Rastgele belirlenmez</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Teknik analiz verilerine dayanır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Senaryonun temel referans noktasıdır</span>
                </li>
              </ul>
            </section>

            {/* TP Seviyeleri */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-400" />
                </div>
                TP (Hedef) Seviyeleri Nasıl Okunur?
              </h2>
              
              <p className="text-slate-300 mb-4">
                TP seviyeleri, fiyatın ulaşması beklenen olası hedef bölgeleridir.
              </p>
              
              <p className="text-slate-300 mb-4">Çoğu sinyal sisteminde:</p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3 p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">TP1</Badge>
                  <span className="text-slate-300">İlk hedef</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">TP2</Badge>
                  <span className="text-slate-300">Ana hedef</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-cyan-900/20 rounded-lg border border-cyan-500/20">
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">TP3</Badge>
                  <span className="text-slate-300">Güçlü trend senaryosu</span>
                </div>
              </div>
              
              <p className="text-slate-400">
                Bu yapı piyasa hareketine göre esneklik sağlar.
              </p>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">AVAX-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giriş: $38.50 | TP1: $40.20 | TP2: $42.80 | Stop: $37.10
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Sinyal kartı: Yön + TP + Stop bir arada</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Bu örnek gibi canlı sinyalleri Borsamex'te anında görebilirsiniz.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Canlı Sinyalleri Gör <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Stop Loss */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Stop Loss Seviyesi Nasıl Değerlendirilir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Stop Loss, senaryonun geçersiz sayıldığı risk sınırıdır.
              </p>
              
              <p className="text-slate-300 mb-4">Bu seviye:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Risk kontrolü sağlar</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Senaryo mantığını korur</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Kontrolsüz zararı engellemeyi amaçlar</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    TP ve Stop Loss Rehberi →
                  </Button>
                </Link>
              </div>
            </section>

            {/* LONG ve SHORT */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-purple-400" />
                </div>
                LONG ve SHORT Bilgisi
              </h2>
              
              <p className="text-slate-300 mb-4">Sinyalde yer alan yön bilgisi:</p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-green-400" />
                      <span className="font-bold text-white">LONG</span>
                    </div>
                    <p className="text-slate-300 text-sm">Yukarı yönlü senaryo</p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="h-5 w-5 text-red-400" />
                      <span className="font-bold text-white">SHORT</span>
                    </div>
                    <p className="text-slate-300 text-sm">Aşağı yönlü senaryo</p>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-slate-400">
                Bu ifade beklentiyi belirtir, kesinlik içermez.
              </p>

              <div className="flex gap-4 mt-4">
                <Link to="/rehber/long-ve-short-ne-demek">
                  <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    LONG ve SHORT Rehberi →
                  </Button>
                </Link>
              </div>
            </section>

            {/* Sinyal Kartı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-amber-400" />
                </div>
                Sinyal Kartı Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Bir sinyal kartı, tüm işlem parametrelerini tek ekranda sunar.
              </p>
              
              <p className="text-slate-300 mb-4">Tipik bir kartta:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Coin / parite</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Giriş fiyatı</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>TP seviyeleri</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Stop seviyesi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Sistem yorumu (varsa)</span>
                </li>
              </ul>
              
              <Card className="border-slate-700/50 bg-slate-900/30">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Amaç hızlı karar değil, <strong className="text-white">net görünürlük</strong> sağlamaktır.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Yaygın Hata */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                </div>
                Yaygın Hata
              </h2>
              
              <p className="text-slate-300 mb-4">Yeni kullanıcılar genellikle:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Sinyali kesinlik gibi algılar</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Stop seviyesini göz ardı eder</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Tek hedefe odaklanır</span>
                </li>
              </ul>
              
              <p className="text-slate-300 mb-4">Oysa profesyonel yaklaşım:</p>
              
              <div className="flex items-start gap-3 text-slate-300">
                <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                <span><strong className="text-white">Senaryo + risk birlikte değerlendirilir</strong></span>
              </div>
            </section>

            {/* Borsamex */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Sistem Yaklaşımı
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Tüm seviyeler algoritmik analiz ile hesaplanır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk yapısı önceliklendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI yalnızca açıklama & bağlam üretir</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/ai-kripto-sinyalleri">
                  <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    AI Sinyalleri Hakkında →
                  </Button>
                </Link>
              </div>
            </section>

            {/* İlgili Konular - Internal Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                İlgili Konular
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/kripto-sinyali-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</p>
                          <p className="text-xs text-slate-500">Temel kavramları öğren</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</p>
                          <p className="text-xs text-slate-500">Hedef ve risk seviyeleri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/long-ve-short-ne-demek" className="group">
                  <Card className="border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-blue-400 transition-colors">LONG ve SHORT Ne Demek?</p>
                          <p className="text-xs text-slate-500">Yön kavramlarını anla</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/btc-sinyalleri" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-amber-400 text-lg">₿</span>
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">BTC Sinyalleri</p>
                          <p className="text-xs text-slate-500">Bitcoin özel sinyaller</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            {/* Önemli Not */}
            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Önemli Not</h3>
                      <p className="text-slate-300">
                        Kripto sinyalleri garanti içermez. Tüm işlemler risk barındırır.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Kripto Sinyali Nasil Okunur? TP, Stop ve Hedef Mantigi"
              url="/rehber/kripto-sinyali-nasil-okunur"
              hashtags="#kripto #sinyal #trading #bitcoin"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/long-ve-short-ne-demek" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              LONG ve SHORT Ne Demek?
            </Link>
            <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: Risk Yönetimi
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-amber-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Sinyal Okumayı Öğrendin, Şimdi Canlı Dene
            </h3>
            <p className="text-slate-400 mb-6">
              Ücretsiz kaydol ve gerçek sinyal kartlarını incele.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default SinyalNasilOkunurPage;
